import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, ShieldAlert, ShieldCheck, RefreshCw, RotateCcw,
  AlertTriangle, CheckCircle2, Clock, Hash, Database
} from 'lucide-react';
import { erpToast } from './erp/ERPToast';
import { API, apiFetch } from '../lib/api';

/**
 * Compact dashboard pill — used in Command Center.
 * onClick navigates to Licensor Console → Pricing & Tiers → Integrity sub-section.
 */
export function TierIntegrityPill({ onNavigate }) {
  const [status, setStatus] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await apiFetch(`${API}/uams/integrity-status`);
      if (!r.ok) return;
      setStatus(await r.json());
    } catch (_) { /* swallow */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(t);
  }, [load]);

  if (!status) return null;
  const { overall, tamperedCount, verifiedCount, totalCount } = status;

  let bg, border, color, Icon, label;
  if (overall === 'tampered') {
    bg = '#fef2f2'; border = '#fecaca'; color = '#b91c1c'; Icon = ShieldAlert;
    label = `License Tier TAMPERED · ${tamperedCount}/${totalCount}`;
  } else if (overall === 'clean') {
    bg = '#f0fdf4'; border = '#bbf7d0'; color = '#15803d'; Icon = ShieldCheck;
    label = `Tier Integrity · ${verifiedCount}/${totalCount} verified`;
  } else {
    bg = '#f8fafc'; border = '#e2e8f0'; color = '#475569'; Icon = Shield;
    label = 'Tier Integrity · not yet pushed';
  }

  return (
    <button
      type="button"
      onClick={onNavigate}
      data-testid="tier-integrity-pill"
      title={overall === 'tampered'
        ? 'Click to view tampered tiers and restore from master'
        : 'Click to view tier integrity details'}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '6px 12px', borderRadius: 999,
        background: bg, border: `1px solid ${border}`, color,
        fontSize: 11, fontWeight: 700, letterSpacing: 0.2,
        cursor: 'pointer'
      }}
    >
      <Icon size={14} /> {label}
      {overall === 'tampered' && (
        <span data-testid="tier-integrity-pill-pulse" style={{
          width: 8, height: 8, borderRadius: '50%',
          background: '#dc2626', animation: 'pulse 1.4s ease-in-out infinite'
        }} />
      )}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(1.4); }
        }
      `}</style>
    </button>
  );
}

/**
 * Full panel — used inside the Licensor Console → Pricing & Tiers tab.
 * Lists each tier with hash + status + actions.
 *
 *   industryFilter: '*' (default — show all rows) or a specific IndustryCode
 *                   to scope the table and overall counts to one industry.
 */
export default function TierIntegrityPanel({ industryFilter = '*' } = {}) {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [reSnapping, setReSnapping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/uams/integrity-status`);
      const d = await r.json();
      setStatus(d);
    } catch (e) { erpToast.error(`Failed to load integrity status: ${e.message}`); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const verifyNow = async () => {
    setVerifying(true);
    try {
      const r = await apiFetch(`${API}/uams/verify-integrity`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok || d.error) {
        erpToast.error(`Verification failed: ${d.error || r.status}`);
      } else if (d.status === 'TAMPERED') {
        erpToast.error(`TAMPER DETECTED on ${d.tiers.filter(t => t.tampered).length} tier(s)`);
      } else if (d.status === 'CLEAN') {
        erpToast.success('All tiers verified — no tampering detected.');
      } else if (d.status === 'PROD_OFFLINE') {
        erpToast.error('Production DB unreachable — cannot verify.');
      } else if (d.status === 'NO_SNAPSHOT') {
        erpToast.error('No snapshot yet. Push the matrix to production first.');
      }
      await load();
    } catch (e) { erpToast.error(`Verify failed: ${e.message}`); }
    setVerifying(false);
  };

  const restore = async () => {
    if (!window.confirm('⚠ Restore from Master\n\nThis will overwrite the production matrix tables (dbo.UAMS_TierMatrix_*) with the trusted local matrix. Any unauthorised changes in production will be wiped.\n\nProceed?')) return;
    setRestoring(true);
    try {
      const r = await apiFetch(`${API}/uams/restore-from-master`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok || d.error) {
        erpToast.error(`Restore failed: ${d.error || r.status}`);
      } else {
        erpToast.success(`Restored ${d.push?.pushed || 0} rows · verify: ${d.verify?.status}`);
      }
      await load();
    } catch (e) { erpToast.error(`Restore failed: ${e.message}`); }
    setRestoring(false);
  };

  const reSnapshotAll = async () => {
    if (!window.confirm('Re-Snapshot All Tiers\n\nThis will re-stamp all tier × industry integrity hashes from the CURRENT local DB state.\nUse this to clear false tamper warnings after a migration or normalization run.\n\nProceed?')) return;
    setReSnapping(true);
    try {
      const r = await apiFetch(`${API}/uams/re-snapshot-all`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok || d.error) {
        erpToast.error(`Re-snapshot failed: ${d.error || r.status}`);
      } else {
        erpToast.success(`Re-stamped ${d.stampedCount || 0} tier×industry hashes — tamper flags cleared`);
      }
      await load();
    } catch (e) { erpToast.error(`Re-snapshot failed: ${e.message}`); }
    setReSnapping(false);
  };

  if (!status) {
    return <div style={{ padding: 24, fontSize: 12, color: '#64748b' }}>Loading integrity status…</div>;
  }

  // Scope rows by the industry filter (if set). The overall pill counts are
  // recomputed against the filtered set so the user sees consistent numbers.
  const rawTiers = status.tiers || [];
  const filtered = (industryFilter && industryFilter !== '*')
    ? rawTiers.filter(t => t.industry === industryFilter)
    : rawTiers;

  const tamperedCount = filtered.filter(t => t.tampered).length;
  const verifiedCount = filtered.filter(t => t.lastVerifiedAt).length;
  const totalCount    = filtered.length;
  const overall = !totalCount || !verifiedCount
    ? 'neutral'
    : (tamperedCount > 0 ? 'tampered' : 'clean');
  const asOf = status.asOf;
  // Shadow the variables used downstream so the existing JSX continues to work.
  const tiers = filtered;

  let headerBg, headerColor, HeaderIcon, headerText;
  if (overall === 'tampered') {
    headerBg = 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)'; headerColor = '#7f1d1d'; HeaderIcon = ShieldAlert;
    headerText = `${tamperedCount} of ${totalCount} tier${tamperedCount > 1 ? 's' : ''} TAMPERED`;
  } else if (overall === 'clean') {
    headerBg = 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'; headerColor = '#064e3b'; HeaderIcon = ShieldCheck;
    headerText = `${verifiedCount} of ${totalCount} tiers verified · all clean`;
  } else {
    headerBg = 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)'; headerColor = '#0f172a'; HeaderIcon = Shield;
    headerText = 'No snapshot yet — push the matrix to production to enable integrity checks';
  }

  return (
    <div data-testid="tier-integrity-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 18px', borderRadius: 10, background: headerBg, color: headerColor
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <HeaderIcon size={26} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: 0.2 }}>Production Tier Matrix Integrity</div>
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{headerText}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            className="erp-btn"
            onClick={verifyNow}
            disabled={verifying || loading}
            data-testid="tier-integrity-verify-btn"
            title="Read the production matrix tables now, recompute hashes, and compare with the trusted local fingerprint."
          >
            <RefreshCw size={12} className={verifying ? 'spin' : ''} />
            {verifying ? 'Verifying…' : 'Verify Now'}
          </button>
          <button
            className="erp-btn"
            onClick={reSnapshotAll}
            disabled={reSnapping || loading}
            data-testid="tier-integrity-re-snapshot-btn"
            title="Re-stamp all tier × industry hashes from the current local DB. Use after normalization/migration to clear false tamper flags."
            style={{ background: '#1d4ed8', color: 'white', border: '1px solid #1e40af' }}
          >
            <Database size={12} className={reSnapping ? 'spin' : ''} />
            {reSnapping ? 'Re-Stamping…' : 'Re-Snapshot All'}
          </button>
          {overall === 'tampered' && (
            <button
              className="erp-btn erp-btn-danger"
              onClick={restore}
              disabled={restoring}
              data-testid="tier-integrity-restore-btn"
              title="Re-push the trusted local matrix to production, overwriting any tampered values."
              style={{ background: '#dc2626', color: 'white', borderColor: '#b91c1c' }}
            >
              <RotateCcw size={12} className={restoring ? 'spin' : ''} />
              {restoring ? 'Restoring…' : 'Restore from Master'}
            </button>
          )}
        </div>
      </div>

      {/* As-of strip */}
      <div style={{ fontSize: 10, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Clock size={11} /> Status as of {new Date(asOf).toLocaleString()}
      </div>

      {/* Per-tier table */}
      {tiers.length === 0 ? (
        <div style={{
          padding: 24, textAlign: 'center', fontSize: 12, color: '#64748b',
          border: '1px dashed #cbd5e1', borderRadius: 8
        }}>
          No integrity snapshots yet. Use <strong>Push Matrix → Production</strong> in the Module Privileges tab
          to seed the first set of fingerprints.
        </div>
      ) : (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead style={{ background: '#f1f5f9', color: '#475569' }}>
              <tr>
                <th style={th}>Industry</th>
                <th style={th}>Tier</th>
                <th style={th}>Status</th>
                <th style={th}>Expected Hash</th>
                <th style={th}>Observed Hash</th>
                <th style={th}>Last Pushed</th>
                <th style={th}>Last Verified</th>
                <th style={th}>Tamper Details</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map(t => (
                <TierRow key={`${t.industry || '*'}::${t.tier}`} t={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Help text */}
      <div style={{
        padding: 12, borderRadius: 6, border: '1px solid #e2e8f0',
        background: 'var(--bg-surface-2, #f8fafc)', fontSize: 11, color: '#475569'
      }}>
        <strong>How it works:</strong> Every time you push the tier matrix to production, the system stores a SHA-256 fingerprint
        per tier locally (and in the blockchain audit chain). <strong>Verify Now</strong> reads the production matrix,
        recomputes the fingerprint, and compares — any drift (someone toggling a BIT column to unlock a paid feature for a Lite tier, etc.)
        is flagged as <span style={{ color: '#b91c1c', fontWeight: 700 }}>TAMPERED</span> with a per-tier diff (added / removed entity IDs).
        <strong> Restore from Master</strong> re-pushes the trusted local matrix to overwrite the tampered prod data.
      </div>
    </div>
  );
}

function TierRow({ t }) {
  const [open, setOpen] = useState(false);
  const tampered = t.tampered;
  const unlimited = t.unlimited;
  const diff = t.tamperDetails || {};
  const hasDiff = ['modules', 'subModules', 'screens'].some(k => {
    const d = diff[k] || {};
    return (d.added && d.added.length) || (d.removed && d.removed.length);
  });

  return (
    <>
      <tr style={{
        borderTop: '1px solid #e2e8f0',
        background: tampered ? '#fef2f2' : (unlimited ? 'rgba(168, 85, 247, 0.04)' : 'var(--bg-surface, #ffffff)'),
        color: tampered ? '#7f1d1d' : '#0f172a'
      }} data-testid={`tier-integrity-row-${(t.industry || 'ALL')}-${t.tier.replace(/\s+/g, '-')}`}>
        <td style={td}>
          <div style={{
            display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
            background: t.industry === '*' ? '#e2e8f0' : '#dbeafe',
            color:      t.industry === '*' ? '#475569' : '#1e40af'
          }}>
            {t.industry === '*' ? 'ALL · baseline' : t.industry}
          </div>
        </td>
        <td style={td}>
          <div style={{ fontWeight: 700, fontSize: 12 }}>{t.tier}</div>
          {unlimited && <div style={{ fontSize: 9, color: '#7c3aed', fontWeight: 700 }}>UNLIMITED — bypasses ACL</div>}
        </td>
        <td style={td}>
          {tampered ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, background: '#fee2e2', color: '#b91c1c', fontWeight: 700, fontSize: 10 }}>
              <AlertTriangle size={11} /> TAMPERED
            </span>
          ) : t.lastVerifiedAt ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, background: '#d1fae5', color: '#065f46', fontWeight: 700, fontSize: 10 }}>
              <CheckCircle2 size={11} /> CLEAN
            </span>
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, background: '#e2e8f0', color: '#475569', fontWeight: 700, fontSize: 10 }}>
              <Clock size={11} /> NOT VERIFIED
            </span>
          )}
        </td>
        <td style={{ ...td, fontFamily: 'monospace', fontSize: 10 }}>
          {t.expectedHash ? (
            <span title={t.expectedHash}><Hash size={10} /> {t.expectedHash.slice(0, 12)}…</span>
          ) : <span style={{ color: '#94a3b8' }}>—</span>}
        </td>
        <td style={{ ...td, fontFamily: 'monospace', fontSize: 10 }}>
          {t.lastVerifiedHash ? (
            <span title={t.lastVerifiedHash} style={{ color: tampered ? '#b91c1c' : 'inherit' }}>
              <Hash size={10} /> {t.lastVerifiedHash.slice(0, 12)}…
            </span>
          ) : <span style={{ color: '#94a3b8' }}>—</span>}
        </td>
        <td style={td}>
          <div style={{ fontSize: 10 }}>{t.lastPushedAt ? new Date(t.lastPushedAt).toLocaleString() : '—'}</div>
          <div style={{ fontSize: 9, color: '#64748b' }}>{t.lastPushedBy || ''}</div>
        </td>
        <td style={{ ...td, fontSize: 10 }}>{t.lastVerifiedAt ? new Date(t.lastVerifiedAt).toLocaleString() : '—'}</td>
        <td style={td}>
          {tampered && hasDiff ? (
            <button
              onClick={() => setOpen(o => !o)}
              data-testid={`tier-integrity-diff-toggle-${(t.industry || 'ALL')}-${t.tier.replace(/\s+/g, '-')}`}
              className="erp-btn"
              style={{ padding: '2px 8px', fontSize: 10 }}
            >
              {open ? 'Hide' : 'Show'} diff
            </button>
          ) : <span style={{ color: '#94a3b8', fontSize: 10 }}>—</span>}
        </td>
      </tr>
      {open && tampered && hasDiff && (
        <tr style={{ background: '#fef2f2' }}>
          <td colSpan={8} style={{ padding: 12, borderTop: '1px solid #fecaca' }}>
            <DiffBlock title="Modules"    d={diff.modules    || {}} />
            <DiffBlock title="SubModules" d={diff.subModules || {}} />
            <DiffBlock title="Screens"    d={diff.screens    || {}} />
            <div style={{ fontSize: 10, color: '#7f1d1d', marginTop: 6, fontStyle: 'italic' }}>
              <strong>added</strong> = entities unlocked in production but NOT permitted by master (potential leak).<br/>
              <strong>removed</strong> = entities permitted by master but missing from production (locked-out users).
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DiffBlock({ title, d }) {
  const added = d.added || [];
  const removed = d.removed || [];
  if (!added.length && !removed.length) return null;
  return (
    <div style={{ marginBottom: 6, fontSize: 11 }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{title}</div>
      {added.length > 0 && (
        <div><span style={{ color: '#b91c1c', fontWeight: 700 }}>+ added ({added.length}):</span>{' '}
          <code style={{ fontSize: 10 }}>{added.slice(0, 30).join(', ')}{added.length > 30 ? ` …+${added.length - 30}` : ''}</code>
        </div>
      )}
      {removed.length > 0 && (
        <div><span style={{ color: '#0369a1', fontWeight: 700 }}>− removed ({removed.length}):</span>{' '}
          <code style={{ fontSize: 10 }}>{removed.slice(0, 30).join(', ')}{removed.length > 30 ? ` …+${removed.length - 30}` : ''}</code>
        </div>
      )}
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 10px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.05 };
const td = { padding: '8px 10px', verticalAlign: 'top' };
